// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
import { arrForEach, arrIndexOf, isNullOrUndefined, mathFloor, mathMin, objCreate, objForEachKey, strTrim } from "@nevware21/ts-utils";
import { _DYN_CONFIG, _DYN_COUNT, _DYN_GET_UTCDATE, _DYN_INTERVAL, _DYN_LENGTH, _DYN_PRE_TRIGGER_DATE, _DYN_PUSH, _DYN_SAMPLING_RATE, _DYN_STRINGIFY, _DYN_THROTTLE_MGR_CFG } from "../__DynamicConstants";
import { onConfigChange } from "../config/DynamicConfig";
import { STR_DISABLED } from "../constants/InternalConstants";
import { _throwInternal, safeGetLogger } from "../diagnostics/DiagnosticLogger";
import { isFeatureEnabled } from "../utils/HelperFuncs";
import { randomValue } from "../utils/RandomHelper";
import { utlCanUseLocalStorage, utlGetLocalStorage, utlSetLocalStorage } from "../utils/StorageHelperFuncs";
var THROTTLE_STORAGE_PREFIX = "appInsightsThrottle";
var FEATURE_STORAGE_PREFIX = "feature-";
var ThrottleMgr = /** @class */ (function () {
    function ThrottleMgr(core, namePrefix) {
        var _self = this;
        var _canUseLocalStorage;
        var _logger;
        var _config;
        var _featureConfig;
        var _localStorageObj;
        var _isTriggered; //_isTriggered is to make sure that we only trigger throttle once a day
        var _namePrefix;
        var _queue;
        var _isReady = false;
        _initConfig();
        // Special internal method to allow the unit tests and DebugPlugin to hook embedded objects
        _self["_getDbgPlgTargets"] = function () {
            return [_queue];
        };
        _self.getConfig = function () {
            return _config;
        };
        /**
         * Check if it is the correct day to send message.
         * If _isTriggered is true, even if canThrottle returns true, message will not be sent,
         * because we only allow triggering sendMessage() once a day.
         * @returns if the current date is the valid date to send message
         */
        _self.canThrottle = function (msgId) {
            var localObj = _getLocalStorageObjByKey(msgId);
            var cfg = _getCfgByKey(msgId);
            return _canThrottle(cfg, _canUseLocalStorage, localObj);
        };
        /**
         * Run a feature operation when enabled by featureOptIn and allowed by its named throttle configuration.
         * @param feature - The featureOptIn and throttleMgrCfg key.
         * @param callback - The feature operation to run when allowed.
         * @param sdkDefaultState - The default state when featureOptIn does not define the feature.
         * @returns The result of applying the throttle, or null when queued or disabled by featureOptIn.
         */
        _self.useFeature = function (feature, callback, sdkDefaultState) {
            if (!feature || !callback || isFeatureEnabled(feature, core[_DYN_CONFIG /* @min:%2econfig */], sdkDefaultState) !== true) {
                return null;
            }
            return _flushCallback(feature, callback, true, true, sdkDefaultState);
        };
        /**
         * Check if throttle is triggered on current day(UTC)
         * if canThrottle returns false, isTriggered will return false
         * @returns if throttle is triggered on current day(UTC)
         */
        _self.isTriggered = function (msgId) {
            return _isTrigger(msgId);
        };
        /**
         * Before isReady set to true, all message will be stored in queue.
         * Message will only be sent out after isReady set to true.
         * Initial and default value: false
         * @returns isReady state
         */
        _self.isReady = function () {
            return _isReady;
        };
        /**
         * Flush all message with given message key in queue with isReady state set to true.
         * @returns if message queue is flushed
         */
        _self.flush = function (msgId) {
            return _flushQueue(_getMapKey(msgId, false));
        };
        /**
         * Flush all message in queue with isReady state set to true.
         * @returns if message queue is flushed
         */
        _self.flushAll = function () {
            try {
                if (_queue) {
                    var result_1 = true;
                    objForEachKey(_queue, function (key) {
                        var isFlushed = _flushQueue(key);
                        result_1 = result_1 && isFlushed;
                    });
                    return result_1;
                }
            }
            catch (err) {
                // eslint-disable-next-line no-empty
            }
            return false;
        };
        /**
         * Set isReady State
         * if isReady set to true, message queue will be flushed automatically.
         * @param isReady - isReady State
         * @pa
         * @returns if message queue is flushed
         */
        _self.onReadyState = function (isReady, flushAll) {
            if (flushAll === void 0) { flushAll = true; }
            _isReady = isNullOrUndefined(isReady) ? true : isReady;
            if (_isReady && flushAll) {
                return _self.flushAll();
            }
            return null;
        };
        _self.sendMessage = function (msgID, message, severity) {
            return _flushCallback(msgID, function () {
                _sendMessage(msgID, _logger, message, severity);
            }, true, false);
        };
        function _flushCallback(key, callback, saveUnsent, isFeature, sdkDefaultState) {
            if (isFeature && isFeatureEnabled(key + "", core[_DYN_CONFIG /* @min:%2econfig */], sdkDefaultState) !== true) {
                return null;
            }
            if (_isReady) {
                var cfg = _getCfgByKey(key, isFeature);
                if (isFeature && !cfg) {
                    callback();
                    return {
                        isThrottled: true,
                        throttleNum: 1
                    };
                }
                var isSampledIn = _canSampledIn(cfg);
                if (!isSampledIn) {
                    return null;
                }
                var localStorageObj = _getLocalStorageObjByKey(key, isFeature);
                var canThrottle = _canThrottle(cfg, _canUseLocalStorage, localStorageObj);
                var throttled = false;
                var number = 0;
                var isTriggered = _isTrigger(key, isFeature);
                var mapKey = _getMapKey(key, isFeature);
                try {
                    if (canThrottle && !isTriggered) {
                        number = mathMin(cfg.limit.maxSendNumber, localStorageObj[_DYN_COUNT /* @min:%2ecount */] + 1);
                        localStorageObj[_DYN_COUNT /* @min:%2ecount */] = 0;
                        throttled = true;
                        _isTriggered[mapKey] = true;
                        localStorageObj[_DYN_PRE_TRIGGER_DATE /* @min:%2epreTriggerDate */] = new Date();
                    }
                    else {
                        _isTriggered[mapKey] = canThrottle;
                        localStorageObj[_DYN_COUNT /* @min:%2ecount */] += 1;
                    }
                    var localStorageName = _getLocalStorageName(key, _namePrefix, isFeature);
                    _resetLocalStorage(_logger, localStorageName, localStorageObj);
                    for (var i = 0; i < number; i++) {
                        callback();
                    }
                }
                catch (e) {
                    // eslint-disable-next-line no-empty
                }
                return {
                    isThrottled: throttled,
                    throttleNum: number
                };
            }
            else {
                if (!!saveUnsent) {
                    var queue = _getQueueByKey(key, isFeature);
                    queue[_DYN_PUSH /* @min:%2epush */]({
                        key: key,
                        callback: callback,
                        isFeature: !!isFeature,
                        sdkDefaultState: sdkDefaultState
                    });
                }
            }
            return null;
        }
        function _flushQueue(mapKey) {
            try {
                var queue = _queue[mapKey];
                if (queue && queue[_DYN_LENGTH /* @min:%2elength */] > 0) {
                    var items = queue.slice(0);
                    _queue[mapKey] = [];
                    arrForEach(items, function (item) {
                        _flushCallback(item.key, item.callback, false, item.isFeature, item.sdkDefaultState);
                    });
                    return true;
                }
            }
            catch (err) {
                // eslint-disable-next-line no-empty
            }
            return false;
        }
        function _initConfig() {
            _logger = safeGetLogger(core);
            _isTriggered = objCreate(null);
            _localStorageObj = objCreate(null);
            _queue = objCreate(null);
            _config = objCreate(null);
            _featureConfig = objCreate(null);
            _setCfgByKey(109 /* _eInternalMessageId.DefaultThrottleMsgKey */);
            _namePrefix = !isNullOrUndefined(namePrefix) ? namePrefix : "";
            core.addUnloadHook(onConfigChange(core[_DYN_CONFIG /* @min:%2econfig */], function (details) {
                var coreConfig = details.cfg;
                _canUseLocalStorage = utlCanUseLocalStorage();
                var configMgr = coreConfig[_DYN_THROTTLE_MGR_CFG /* @min:%2ethrottleMgrCfg */] || {};
                objForEachKey(configMgr, function (key, cfg) {
                    _setCfgByKey(key, cfg, true);
                    var msgId = parseInt(key);
                    if (msgId + "" === key) {
                        _setCfgByKey(msgId, cfg, false);
                    }
                });
            }));
        }
        function _getCfgByKey(key, isFeature) {
            if (isFeature) {
                var feature = key + "";
                var coreConfig = core[_DYN_CONFIG /* @min:%2econfig */];
                var featureCfg = coreConfig[_DYN_THROTTLE_MGR_CFG /* @min:%2ethrottleMgrCfg */] && coreConfig[_DYN_THROTTLE_MGR_CFG /* @min:%2ethrottleMgrCfg */][feature];
                if (featureCfg) {
                    _setCfgByKey(feature, featureCfg, true);
                }
                else {
                    delete _featureConfig[feature];
                }
                return _featureConfig[feature];
            }
            return _config[+key] || _config[109 /* _eInternalMessageId.DefaultThrottleMsgKey */];
        }
        function _setCfgByKey(key, config, isFeature) {
            var _a, _b;
            try {
                var cfg = config || {};
                var curCfg = {};
                curCfg[STR_DISABLED /* @min:%2edisabled */] = !!cfg[STR_DISABLED /* @min:%2edisabled */];
                var configInterval = cfg[_DYN_INTERVAL /* @min:%2einterval */] || {};
                curCfg[_DYN_INTERVAL /* @min:%2einterval */] = _getIntervalConfig(configInterval);
                var limit = {
                    samplingRate: !isNullOrUndefined((_a = cfg.limit) === null || _a === void 0 ? void 0 : _a.samplingRate) ? cfg.limit[_DYN_SAMPLING_RATE /* @min:%2esamplingRate */] : 100,
                    // dafault: every time sent only 1 event
                    maxSendNumber: ((_b = cfg.limit) === null || _b === void 0 ? void 0 : _b.maxSendNumber) || 1
                };
                curCfg.limit = limit;
                if (isFeature) {
                    _featureConfig[key + ""] = curCfg;
                }
                else {
                    _config[+key] = curCfg;
                }
            }
            catch (e) {
                // eslint-disable-next-line no-empty
            }
        }
        function _getIntervalConfig(interval) {
            interval = interval || {};
            var monthInterval = interval === null || interval === void 0 ? void 0 : interval.monthInterval;
            var dayInterval = interval === null || interval === void 0 ? void 0 : interval.dayInterval;
            var isSpecificDaysGiven = (interval === null || interval === void 0 ? void 0 : interval.daysOfMonth) && (interval === null || interval === void 0 ? void 0 : interval.daysOfMonth[_DYN_LENGTH /* @min:%2elength */]) > 0;
            // default: send data every 3 month each year
            if (isNullOrUndefined(monthInterval) && isNullOrUndefined(dayInterval)) {
                interval.monthInterval = 3;
                if (!isSpecificDaysGiven) {
                    // default: send data on 28th
                    interval.daysOfMonth = [28];
                }
            }
            interval = {
                // dafault: sent every three months
                monthInterval: interval === null || interval === void 0 ? void 0 : interval.monthInterval,
                dayInterval: interval === null || interval === void 0 ? void 0 : interval.dayInterval,
                daysOfMonth: interval === null || interval === void 0 ? void 0 : interval.daysOfMonth
            };
            return interval;
        }
        function _canThrottle(config, canUseLocalStorage, localStorageObj) {
            if (config && !config[STR_DISABLED /* @min:%2edisabled */] && canUseLocalStorage && !isNullOrUndefined(localStorageObj)) {
                var curDate = _getThrottleDate();
                var date = localStorageObj.date;
                var interval = config[_DYN_INTERVAL /* @min:%2einterval */];
                var monthCheck = 1;
                if (interval === null || interval === void 0 ? void 0 : interval.monthInterval) {
                    var monthExpand = (curDate.getUTCFullYear() - date.getUTCFullYear()) * 12 + curDate.getUTCMonth() - date.getUTCMonth();
                    monthCheck = _checkInterval(interval.monthInterval, 0, monthExpand);
                }
                var dayCheck = 1;
                if ((interval === null || interval === void 0 ? void 0 : interval.daysOfMonth) && interval.daysOfMonth[_DYN_LENGTH /* @min:%2elength */] > 0) {
                    dayCheck = arrIndexOf(interval.daysOfMonth, curDate[_DYN_GET_UTCDATE /* @min:%2egetUTCDate */]());
                }
                else if (interval === null || interval === void 0 ? void 0 : interval.dayInterval) {
                    var daySpan = mathFloor((curDate.getTime() - date.getTime()) / 86400000);
                    dayCheck = _checkInterval(interval.dayInterval, 0, daySpan);
                }
                return monthCheck >= 0 && dayCheck >= 0;
            }
            return false;
        }
        function _getLocalStorageName(key, prefix, isFeature) {
            var fix = !isNullOrUndefined(prefix) ? prefix : "";
            if (key) {
                return THROTTLE_STORAGE_PREFIX + fix + "-" + (isFeature ? FEATURE_STORAGE_PREFIX : "") + key;
            }
            return null;
        }
        // returns if throttle is triggered on current Date
        function _isTriggeredOnCurDate(preTriggerDate) {
            try {
                if (preTriggerDate) {
                    var curDate = new Date();
                    return preTriggerDate.getUTCFullYear() === curDate.getUTCFullYear() &&
                        preTriggerDate.getUTCMonth() === curDate.getUTCMonth() &&
                        preTriggerDate[_DYN_GET_UTCDATE /* @min:%2egetUTCDate */]() === curDate[_DYN_GET_UTCDATE /* @min:%2egetUTCDate */]();
                }
            }
            catch (e) {
                // eslint-disable-next-line no-empty
            }
            return false;
        }
        // transfer local storage string value to object that identifies start date, current count and preTriggerDate
        function _getLocalStorageObj(value, logger, storageName) {
            try {
                var storageObj = {
                    date: _getThrottleDate(),
                    count: 0
                };
                if (value) {
                    var obj = JSON.parse(value);
                    var curObj = {
                        date: _getThrottleDate(obj.date) || storageObj.date,
                        count: obj[_DYN_COUNT /* @min:%2ecount */] || storageObj[_DYN_COUNT /* @min:%2ecount */],
                        preTriggerDate: obj.preTriggerDate ? _getThrottleDate(obj[_DYN_PRE_TRIGGER_DATE /* @min:%2epreTriggerDate */]) : undefined
                    };
                    return curObj;
                }
                else {
                    _resetLocalStorage(logger, storageName, storageObj);
                    return storageObj;
                }
            }
            catch (e) {
                // eslint-disable-next-line no-empty
            }
            return null;
        }
        // if datestr is not defined, current date will be returned
        function _getThrottleDate(dateStr) {
            // if new Date() can't be created through the provided dateStr, null will be returned.
            try {
                if (dateStr) {
                    var date = new Date(dateStr);
                    //make sure it is a valid Date Object
                    if (!isNaN(date.getDate())) {
                        return date;
                    }
                }
                else {
                    return new Date();
                }
            }
            catch (e) {
                // eslint-disable-next-line no-empty
            }
            return null;
        }
        function _resetLocalStorage(logger, storageName, obj) {
            try {
                return utlSetLocalStorage(logger, storageName, strTrim(JSON[_DYN_STRINGIFY /* @min:%2estringify */](obj)));
            }
            catch (e) {
                // eslint-disable-next-line no-empty
            }
            return false;
        }
        function _checkInterval(interval, start, current) {
            if (interval <= 0) {
                return 1;
            }
            // count from start year
            return (current >= start) && (current - start) % interval == 0 ? mathFloor((current - start) / interval) + 1 : -1;
        }
        function _sendMessage(msgID, logger, message, severity) {
            _throwInternal(logger, severity || 1 /* eLoggingSeverity.CRITICAL */, msgID, message);
        }
        // NOTE: config.limit.samplingRate is set to 4 decimal places,
        // so config.limit.samplingRate = 1 means 0.0001%
        function _canSampledIn(cfg) {
            try {
                return cfg.limit[_DYN_SAMPLING_RATE /* @min:%2esamplingRate */] > 0 && randomValue(1000000) <= cfg.limit[_DYN_SAMPLING_RATE /* @min:%2esamplingRate */];
            }
            catch (e) {
                // eslint-disable-next-line no-empty
            }
            return false;
        }
        function _getMapKey(key, isFeature) {
            return (isFeature ? FEATURE_STORAGE_PREFIX : "") + key;
        }
        function _getLocalStorageObjByKey(key, isFeature) {
            try {
                var mapKey = _getMapKey(key, isFeature);
                var curObj = _localStorageObj[mapKey];
                if (!curObj) {
                    var localStorageName = _getLocalStorageName(key, _namePrefix, isFeature);
                    curObj = _getLocalStorageObj(utlGetLocalStorage(_logger, localStorageName), _logger, localStorageName);
                    _localStorageObj[mapKey] = curObj;
                }
                return _localStorageObj[mapKey];
            }
            catch (e) {
                // eslint-disable-next-line no-empty
            }
            return null;
        }
        function _isTrigger(key, isFeature) {
            var mapKey = _getMapKey(key, isFeature);
            var isTrigger = false;
            var localStorageObj = _getLocalStorageObjByKey(key, isFeature);
            if (localStorageObj) {
                isTrigger = _isTriggeredOnCurDate(localStorageObj[_DYN_PRE_TRIGGER_DATE /* @min:%2epreTriggerDate */]);
            }
            _isTriggered[mapKey] = isTrigger;
            return isTrigger;
        }
        function _getQueueByKey(key, isFeature) {
            _queue = _queue || objCreate(null);
            var mapKey = _getMapKey(key, isFeature);
            if (isNullOrUndefined(_queue[mapKey])) {
                _queue[mapKey] = [];
            }
            return _queue[mapKey];
        }
    }
    return ThrottleMgr;
}());
export { ThrottleMgr };
//# sourceMappingURL=ThrottleMgr.js.map